import React, { useCallback, useContext, useEffect, useState } from 'react';

import { dispatch } from '../../../../../../Shared/js/redux/store';
import { actions } from '../../../../../js/redux/modalSlice';
import { SearchFilterContext, SearchFilterContextData } from '../SearchFilterContext';
import BundleSelect from './BundleSelect';

function BuildMegaAdvancedState(context: SearchFilterContextData): string[] {
    const returnArray = new Array<string>();
    if (context.criteria.common.maxResultsPerSupplier) returnArray.push('single-result');
    if (context.criteria.staticIp) returnArray.push('static-ip');
    if (context.criteria.uploadNotCounted) returnArray.push('upload-not-counted');
    if (context.criteria.common.showLessResults) returnArray.push('less-results');
    return returnArray;
}

export default function AdvancedTab() {
    //Select Names
    const chooseContractTerms = 'choose-contract-terms';
    const chooseModem = 'choose-modem';
    const choosePrepaidExpiry = 'choose-prepaid-expiry';
    const chooseMegaAdvanced = 'choose-mega-advanced';
    const byoModem = 'BYO-Modem';
    const simOnly = 'sim-only';

    const context = useContext(SearchFilterContext);
    const allContractTerms = context.data.allContractTerms;
    const [contractTermSelectedOptions, setSelectedContractTerms] = useState<Array<string>>(
        context.criteria.common.contractTerms.map(ct => ct.toString())
    );
    const [modemSelectedOptions, setModemSelectedOptions] = useState<string>(
        context.criteria.modemShortUrl === byoModem
            ? context.criteria.simOnly
                ? simOnly
                : byoModem
            : context.criteria.modemShortUrl
    );
    const [prepaidExpirySelectedOptions, setPrepaidExpirySelectedOptions] = useState<string>(
        context.criteria.prepaidExpiry?.valueOf()?.toString() ?? ''
    );
    const [megaAdvancedSelectedOptions, setMegaAdvancedSelectedOptions] = useState<Array<string>>(
        BuildMegaAdvancedState(context)
    );

    const DidUnmount = useCallback((node: HTMLElement) => {
        const dom = jQuery(node);
        const select = dom.find('#' + chooseModem);
        select.off('change', this.onModemChange);
    }, []);

    const DidMount = useCallback(
        (node: HTMLElement) => {
            const dom = jQuery(node);
            WhistleOut.applySelectPickersStyle(dom);
            WhistleOut.applySelectPicker(dom.find('#' + chooseContractTerms));
            WhistleOut.applySelectPicker(dom.find('#' + chooseModem));
            WhistleOut.applySelectPicker(dom.find('#' + choosePrepaidExpiry));
            WhistleOut.applySelectPicker(dom.find('#' + chooseMegaAdvanced));
            return () => DidUnmount(node);
        },
        [DidUnmount]
    );

    useEffect(() => {
        if (contractTermSelectedOptions === undefined || contractTermSelectedOptions.map === undefined) return;

        dispatch(
            actions.updateContractTerms({
                selected: contractTermSelectedOptions.map(ct => Number.parseInt(ct)),
                all: allContractTerms
            })
        );
    }, [contractTermSelectedOptions, allContractTerms]);

    useEffect(() => {
        const isSimOnly = modemSelectedOptions === simOnly;

        dispatch(
            actions.updateModem({
                modemShortUrl: isSimOnly ? byoModem : modemSelectedOptions,
                simOnly: isSimOnly
            })
        );
    }, [modemSelectedOptions]);

    useEffect(() => {
        dispatch(actions.updatePrepaidExpiry(Number.parseInt(prepaidExpirySelectedOptions)));
    }, [prepaidExpirySelectedOptions]);

    useEffect(() => {
        dispatch(actions.updateAdvancedOptions(megaAdvancedSelectedOptions));
    }, [megaAdvancedSelectedOptions]);

    const OnChange = useCallback(e => {
        const options = [...e.target.selectedOptions];
        const values = options.map(o => o.value);
        switch (e.target.id) {
            case chooseContractTerms:
                setSelectedContractTerms(values);
                break;
            case chooseModem:
                setModemSelectedOptions(values[0]);
                break;
            case choosePrepaidExpiry:
                setPrepaidExpirySelectedOptions(values[0]);
                break;
            case chooseMegaAdvanced:
                setMegaAdvancedSelectedOptions(values);
                break;
            default:
                break;
        }
    }, []);

    function renderMobileBroadbandModems() {
        if (context.data.showMobileModems) {
            return (
                <optgroup label={context.data.mobileModemsLabel}>
                    {context.data.mobileModemTypes.map(modem => {
                        return (
                            <option key={modem.key} value={modem.key}>
                                {modem.value}
                            </option>
                        );
                    })}
                </optgroup>
            );
        }
    }

    return (
        <div ref={DidMount}>
            <div className="row">
                <div className="col-md-10">
                    <div className="row pad-b-5">
                        <div className="col-xs-24">
                            <h5 className="font-5">{context.data.resources.contractLength}</h5>
                            <div className="selectpicker-wide">
                                <select
                                    id={chooseContractTerms}
                                    tabIndex={-1}
                                    className="selectpicker bs-select-hidden"
                                    multiple
                                    data-selected-text-format="count>1"
                                    onChange={OnChange}
                                    value={contractTermSelectedOptions ?? []}
                                >
                                    {context.data.contractTerms.map(contractTerm => {
                                        return (
                                            <option key={contractTerm.key} value={contractTerm.key}>
                                                {contractTerm.value}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row pad-y-5">
                        <div className="col-xs-24">
                            <h5 className="font-5">{context.data.resources.modemType}</h5>
                            <div className="selectpicker-wide">
                                <select
                                    id={chooseModem}
                                    tabIndex={-1}
                                    className="selectpicker bs-select-hidden"
                                    onChange={OnChange}
                                    value={modemSelectedOptions ?? ''}
                                >
                                    <option value="">{context.data.anyModemLabel}</option>
                                    <optgroup label={context.data.homeModemsLabel}>
                                        {context.data.homeModemTypes.map(modem => {
                                            return (
                                                <option key={modem.key} value={modem.key}>
                                                    {modem.value}
                                                </option>
                                            );
                                        })}
                                    </optgroup>
                                    {renderMobileBroadbandModems()}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-sm-24 col-md-10 col-md-offset-2">
                    <div className="row pad-b-5">
                        <div className="col-xs-24">
                            <h5 className="font-5">{context.data.resources.prepaidExpiry}</h5>
                            <div className="selectpicker-wide">
                                <select
                                    id="choose-prepaid-expiry"
                                    tabIndex={-1}
                                    className="selectpicker bs-select-hidden"
                                    onChange={OnChange}
                                    value={prepaidExpirySelectedOptions ?? ''}
                                >
                                    {context.data.prepaidExpiryValues.map(expiry => {
                                        return (
                                            <option key={expiry.key} value={expiry.key}>
                                                {expiry.value}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>
                    {context.data.showMegaAdvancedFilters ? (
                        <div className="row pad-y-5">
                            <div className="col-xs-24">
                                <h5 className="font-5">{context.data.resources.superMegaAdvanced}</h5>
                                <div className="selectpicker-wide">
                                    <select
                                        id="choose-mega-advanced"
                                        tabIndex={-1}
                                        className="selectpicker bs-select-hidden"
                                        multiple
                                        data-selected-text-format="count>1"
                                        title={context.data.resources.selectOptions}
                                        value={megaAdvancedSelectedOptions ?? []}
                                        onChange={OnChange}
                                    >
                                        {context.data.megaAdvancedOptions.map(o => {
                                            return (
                                                <option key={o.key} value={o.key}>
                                                    {o.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div className="row pad-y-5">
                        <div className="col-xs-24">
                            <BundleSelect />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
